import ObjectModelBuilderModel from "../tracked/objectModelBuilderModel";
import {ILabelable} from "../../interfaces/propertyInterfaces";
import IRepo from "../../interfaces/repo";
import Repo from "../../state/modules/repo";
import Guid from "../../helpers/guid";
import ObjectModel from "../objectModel";
import GloballySelected from "../../../formability/object_model_builder/store/modules/selected";
import {RelationType} from "../relationType";
import {ObjectModelTriggerCapability} from "~/models/objectModelTriggers/objectModelTriggerCapability";
import {ObjectModelTriggerType} from "~/enums/objectModelTriggerType";
import ObjectModelTriggerCapabilityManager from "~/models/objectModelTriggers/objectModelTriggerCapabilityManager";
import ObjectModelTriggerDefinition from "~/models/objectModelTriggers/objectModelTriggerDefinition";
import {ObjectModelTriggerBasedOnType} from "~/enums/objectModelTriggerBasedOnType";
import RelativeTimePeriod from "~/models/relativeTimePeriod";
import ObjectModelConditionalFields, {
  ObjectModelTriggerConditionalField
} from "~/models/objectModels/objectModelConditionalFields";
import ConditionalRuleSetFactory from "~/models/conditionalRules/conditionalRuleSetFactory";
import {Hash} from "~/interfaces/hashInterfaces";
import ObjectModelConditionalRuleSet from "~/models/objectModels/objectModelConditionalRuleSet";
import IConditionalRuleContainer from "~/models/conditionalRules/conditionalRuleContainer";
import {ObjectHelper} from "~/helpers/objectHelper";
import ObjectModelProperty from "~/models/objectModelProperties/objectModelProperty";
import Interval from "~/models/interval";
import Notification from "~/models/notifications/notification";
import NotificationFactory from "~/models/notifications/notificationFactory";
import ObjectModelTriggerNotification from "~/models/objectModels/objectModelTriggerNotification";
import ObjectModelTriggerNotificationFactory from "~/models/objectModels/objectModelTriggerNotificationFactory";
import PropertyUpdate from "~/models/propertyUpdate";
import ObjectModelRelation from "~/models/objectModelRelations/objectModelRelation";
import RelatedModelUpdate from "~/models/relatedModelUpdate";
import Validatable from "~/mixins/validatable";
import ObjectModelTriggerContainer from "~/mixins/objectModelTriggerContainer";
import ObjectModelTriggerValidation from "~/models/navigableValidations/objectModelTriggerValidation";
import RelatedModelPrimaryKey from "~/models/relatedModelPrimaryKey";
import {ObjectModelTriggerRunsForType} from "~/enums/objectModelTriggerRunsForType";
import {DurationType} from "~/enums/durationType";
import ProcessFlowSubmissionMappedVariable from "~/models/processFlows/processFlowSubmissionMappedVariable";

export default class ObjectModelTrigger extends Validatable(ObjectModelTriggerContainer(ObjectModelBuilderModel)) implements ILabelable, IConditionalRuleContainer {
  except = ['objectModel', 'createEmbeddedObject', 'notifications', 'is_published_time_trigger'];

  trigger_type: ObjectModelTriggerType;
  trigger_based_on_type: ObjectModelTriggerBasedOnType;
  trigger_runs_for_type: ObjectModelTriggerRunsForType;
  is_published_time_trigger: boolean; // read-only from server
  sort_order: number;
  label_title: string;
  linked_form_id: string;
  linked_form_title: string;
  restrict_form_viewers: boolean;
  trigger_conditional_rule_sets: Hash<ObjectModelConditionalRuleSet> = {};
  time_trigger_conditional_rule_sets: Hash<ObjectModelConditionalRuleSet> = {};
  first_trigger: RelativeTimePeriod;
  occurs_at_interval: boolean;
  custom_interval: boolean;
  interval: Interval;
  number_of_occurrences: number;
  object_model_trigger_notifications: { [key: string]: ObjectModelTriggerNotification} = {};
  property_updates:  { [key: string]: PropertyUpdate} = {};
  mapping_id: string;
  mapping_title: string;
  process_flow_id: string;
  process_flow_submission_mapped_variables: { [key: string]: ProcessFlowSubmissionMappedVariable } = {};
  linked_relationship_client_id: string;
  linked_relationship_title: string;
  relationship_id: string;
  inverse: boolean;
  foreign_key: string;
  related_model_updates:  { [key: string]: RelatedModelUpdate} = {};
  related_model_primary_keys: { [key: string]: RelatedModelPrimaryKey} = {};
  static repo: IRepo<ObjectModelTrigger> = new Repo<ObjectModelTrigger>('objectModelTriggers', ObjectModelTrigger);

  constructor() {
    super();
    this.defineProperty('client_id', Guid.create());
    this.defineProperty('trigger_based_on_type', ObjectModelTriggerBasedOnType.ObjectModelProperty);
    if (!this.first_trigger) {
      this.createEmbeddedObject.first_trigger(this, { type: DurationType.propertyBasedTimeSpan });
    }
    if (this.propertyUpdatesArray.length === 0) {
      this.appendPropertyUpdate()
    }
    if (this.relatedModelUpdatesArray.length === 0) {
      this.appendRelatedModelUpdate()
    }
    if (!this.interval) {
      this.createEmbeddedObject.interval(this, {})
    }
    for (const definition of ObjectModelConditionalFields.TRIGGER_FIELDS) {
      const conditionalFieldName = `${definition.fieldName}_conditional_rule_sets`;
      this.except.push(conditionalFieldName);
      this[conditionalFieldName] = {};
      this.createEmbeddedObject[conditionalFieldName] = (parent, json) => {
        return ConditionalRuleSetFactory.createForContainer(this, definition, {}, true, false);
      };
      if (definition.defaultConditionalValue instanceof Function) {
        this.except.push(definition.fieldName);
        this.createEmbeddedObject[definition.fieldName] = definition.defaultConditionalValue;
      }
    }
  }

  initialize(root: ObjectModel, parent: ObjectModel, triggerType: ObjectModelTriggerType, json: object = {}): this {
    this.rootModel = root;
    this.parent = parent;
    this.defineProperty('trigger_type', triggerType);
    this.defineProperty('occurs_at_interval', false);
    this.defineProperty('custom_interval', true);
    this.defineProperty('inverse', null);
    ObjectModelTriggerCapabilityManager.addDefaultProperties(this);
    this.loadFromJson(json);
    return this;
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    const models = {
      'first_trigger': {type: RelativeTimePeriod, relationType: RelationType.singleObject},
      'interval': {type: RelativeTimePeriod, relationType: RelationType.singleObject},
      'object_model_trigger_notifications': { type: ObjectModelTriggerNotification, relationType: RelationType.dictionary },
      'property_updates': {type: PropertyUpdate, relationType: RelationType.dictionary},
      'related_model_updates': {type: RelatedModelUpdate, relationType: RelationType.dictionary},
      'related_model_primary_keys': {type: RelatedModelPrimaryKey, relationType: RelationType.dictionary},
      'process_flow_submission_mapped_variables': {type: ProcessFlowSubmissionMappedVariable, relationType: RelationType.dictionary}
    };
    for (const definition of ObjectModelConditionalFields.TRIGGER_FIELDS) {
      models[`${definition.fieldName}_conditional_rule_sets`] = {type: ObjectModelConditionalRuleSet, relationType: RelationType.dictionary};
      if (definition.defaultConditionalValue instanceof Function) {
        models[definition.fieldName] = {type: Object, relationType: RelationType.singleObject};
      }
    }
    return models;
  }

  createEmbeddedObject = {
    first_trigger: (parent, json): RelativeTimePeriod => new RelativeTimePeriod(parent, 'first_trigger', json),
    interval: (parent, json): Interval => new Interval(parent, 'interval', json),
    object_model_trigger_notifications: (parent, json): ObjectModelTriggerNotification => { return ObjectModelTriggerNotificationFactory.create(parent);},
    property_updates: (parent, json): PropertyUpdate =>  new PropertyUpdate(parent, 'property_updates', json),
    related_model_updates: (parent, json): RelatedModelUpdate =>  new RelatedModelUpdate(parent, 'related_model_updates', json),
    related_model_primary_keys: (parent, json): RelatedModelPrimaryKey => new RelatedModelPrimaryKey(parent, 'related_model_primary_keys', json),
    process_flow_submission_mapped_variables: (parent, json): ProcessFlowSubmissionMappedVariable => new ProcessFlowSubmissionMappedVariable(parent, json)
  };
  appendPropertyUpdate(json = {}){
    this.createEmbeddedObject.property_updates(this, json )
  }

  appendRelatedModelUpdate(json = {}){
    this.createEmbeddedObject.related_model_updates(this, json )
  }
  static get rootName(): string {
    return 'object_model_triggers';
  }

  get linked_object_model_relation(): ObjectModelRelation {
    return ObjectModelRelation.repo.find(this.linked_relationship_client_id);
  }

  get hasLinkedRelation(): boolean {
    return !!this.linked_object_model_relation;
  }
  get rootName(): string {
    return 'object_model_triggers';
  }

  // ******************** VALIDATABLE******************** //
  _validation: ObjectModelTriggerValidation = new ObjectModelTriggerValidation(this);

  get isSelected(): boolean {
    return GloballySelected.objectModelTrigger.is(this);
  }

  get propertyUpdatesArray(): PropertyUpdate[]{
    return ObjectHelper.toArray(this.property_updates)
  }
  get relatedModelUpdatesArray(): RelatedModelUpdate[]{
    return ObjectHelper.toArray(this.related_model_updates)
  }

  get processFlowSubmissionMappedVariables(): ProcessFlowSubmissionMappedVariable[] {
    return ObjectHelper.toArray(this.process_flow_submission_mapped_variables);
  }

  get relatedModelPrimaryKeysArray(): RelatedModelPrimaryKey[]{
    return ObjectHelper.toArray(this.related_model_primary_keys)
  }
  get icon(): string {
    return ObjectModelTriggerDefinition.getIcon(this.trigger_type);
  }

  get displayName(): string {
    return ObjectModelTriggerDefinition.getDisplayName(this.trigger_type);
  }


  get notificationsArray(): Notification[] {
    return ObjectHelper.toArray(this.object_model_trigger_notifications);
  }

  can(duty: ObjectModelTriggerCapability): boolean {
    return ObjectModelTriggerCapabilityManager.can(this.trigger_type, duty);
  }

  addToRepo(): void {
    ObjectModelTrigger.repo.add(this);
  }

  select(): void {
    GloballySelected.objectModelTrigger.set(this);
  }

  get isLast(): boolean {
    return this.sort_order === (<ObjectModel>this.parent).nextSortOrderFor(this.rootName) - 1;
  }

  get isFirst(): boolean {
    return this.sort_order <= 0;
  }

  moveUp(): void {
    if (!this.isFirst) {
      this.sort_order--;
      this.parent.updateTriggerPositions(this, this.sort_order);
    }
  }
  getConditionalRuleSets(fieldName: string): Hash<ObjectModelConditionalRuleSet> {
    return this[`${fieldName}_conditional_rule_sets`];
  }

  activeConditionalRuleSets(): Hash<ObjectModelConditionalRuleSet> {
    if (this.trigger_based_on_type === ObjectModelTriggerBasedOnType.TimeBasedEvent) {
      return this.time_trigger_conditional_rule_sets;
    } else if (this.trigger_based_on_type === ObjectModelTriggerBasedOnType.ObjectModelProperty) {
      return this.trigger_conditional_rule_sets;
    }

    return null;
  }

  getSortedConditionalRuleSets(fieldName: string): ObjectModelConditionalRuleSet[] {
    const ruleSets = this.getConditionalRuleSets(fieldName);
    return ObjectHelper.toArray(ruleSets).sort((x, y) => x.sort_order < y.sort_order ? -1 : 1);
  }

  createNewRuleSet(fieldName: string, definition: ObjectModelTriggerConditionalField): ObjectModelConditionalRuleSet {
    return new ObjectModelConditionalRuleSet(fieldName, definition);
  }

  getReferencedControl(id: string): ObjectModelProperty {
    return ObjectModelProperty.repo.find(id);
  }

  getReferencableControls(): ObjectModelProperty[] {
    return this.rootModel.objectModelPropertiesArray;
  }

  moveDown(): void {
    if (!this.isLast) {
      this.sort_order++;
      this.parent.updateTriggerPositions(this, this.sort_order);
    }
  }

  getDisplayName(propName: string, value?): string {
    switch (propName) {
      case 'trigger_time_interval':
        return 'trigger_time_interval';
      default:
        return propName
    }
  }
}